import { useQuery } from '@tanstack/react-query'
import {
  getStateGapAnalysis,
  getCrosswalkCompleteness,
  getRelationshipColor,
  getRelationshipLabel,
  type StateCfcCrosswalk,
} from '../../../lib/services/stateRegulatory'
import { useState } from 'react'

export function StateDetailPanel({
  stateCode,
  onClose,
}: {
  stateCode: string
  onClose: () => void
}) {
  const [domainFilter] = useState<string>('all')
  const [typeFilter, setTypeFilter] = useState<string>('all')

  const { data: crosswalk, isLoading } = useQuery({
    queryKey: ['state-gap-analysis', stateCode, domainFilter, typeFilter],
    queryFn: () =>
      getStateGapAnalysis(
        stateCode,
        domainFilter !== 'all' ? domainFilter : undefined,
        typeFilter !== 'all' ? typeFilter : undefined
      ),
  })

  const { data: completeness } = useQuery({
    queryKey: ['state-completeness', stateCode],
    queryFn: () => getCrosswalkCompleteness(stateCode),
  })

  return (
    <section className="v11-section space-y-4 p-5">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-bold text-[var(--brand-deep)]">
          {stateCode} — Gap Analysis
        </h2>
        <button className="btn-ghost text-xs" onClick={onClose}>Close</button>
      </div>

      {completeness && (
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
          <StatCard label="Controls Mapped" value={`${completeness.mapped_controls}/${completeness.total_state_controls}`} />
          <StatCard label="Control Coverage" value={`${completeness.coverage_pct}%`} />
          <StatCard label="CfC Sections Covered" value={`${completeness.covered_cfc_sections}/${completeness.total_cfc_sections}`} />
          <StatCard label="CfC Coverage" value={`${completeness.cfc_coverage_pct}%`} />
        </div>
      )}

      <div className="flex gap-2">
        <select
          className="rounded border border-gray-300 px-2 py-1 text-sm"
          value={typeFilter}
          onChange={(e) => setTypeFilter(e.target.value)}
        >
          <option value="all">All Types</option>
          <option value="equivalent">Equivalent</option>
          <option value="stricter">Stricter</option>
          <option value="weaker">Weaker</option>
          <option value="gap">Gap</option>
          <option value="defers_to_cms">Defers to CMS</option>
        </select>
      </div>

      {isLoading ? (
        <div className="py-4 text-center text-gray-400">Loading crosswalk data...</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-3 py-2 text-left font-medium text-gray-600">Control</th>
                <th className="px-3 py-2 text-left font-medium text-gray-600">CfC Section</th>
                <th className="px-3 py-2 text-center font-medium text-gray-600">Type</th>
                <th className="px-3 py-2 text-left font-medium text-gray-600">Gap Analysis</th>
                <th className="px-3 py-2 text-left font-medium text-gray-600">Citations</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {(crosswalk ?? []).map((row, i) => (
                <CrosswalkRow key={i} row={row} />
              ))}
            </tbody>
          </table>
          {(!crosswalk || crosswalk.length === 0) && (
            <div className="py-4 text-center text-gray-400">No crosswalk data found.</div>
          )}
        </div>
      )}
    </section>
  )
}

function StatCard({ label, value }: { label: string; value: string }) {
  return (
    <div className="v11-mini-metric">
      <div className="text-xl font-bold text-[var(--brand-deep)]">{value}</div>
      <div className="text-xs text-gray-500">{label}</div>
    </div>
  )
}

function CrosswalkRow({ row }: { row: StateCfcCrosswalk }) {
  const colorClass = getRelationshipColor(row.relationship_type)

  return (
    <tr className="hover:bg-gray-50">
      <td className="px-3 py-2">
        <div className="font-medium">{row.internal_control_id}</div>
        <div className="text-xs text-gray-400">{row.control_title}</div>
      </td>
      <td className="px-3 py-2">
        <div>{row.cfr_section}{row.cfr_subsection ?? ''}</div>
        <div className="text-xs text-gray-400">{row.cfr_name}</div>
      </td>
      <td className="px-3 py-2 text-center">
        <span className={`inline-block rounded-full px-2 py-0.5 text-xs font-medium ${colorClass}`}>
          {getRelationshipLabel(row.relationship_type)}
        </span>
      </td>
      <td className="max-w-xs px-3 py-2 text-xs text-gray-600">
        {row.gap_analysis_summary ?? '—'}
      </td>
      <td className="px-3 py-2 text-xs">
        <div className="text-gray-500">State: {row.state_citation ?? '—'}</div>
        <div className="text-gray-500">CMS: {row.cms_cfc_citation ?? '—'}</div>
      </td>
    </tr>
  )
}
